"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";
import { useRecentClients } from "@/hooks/use-recent-clients";
import { loadClientList, type ClientListEntry } from "@/lib/clients/client-list-cache";
import { anonymizeClientName } from "@/lib/demo-mode";

/**
 * Company switcher, moved out of the sidebar into the top bar (2026-08-01).
 * It now renders on every authenticated page, not just inside a company, so
 * the agency surfaces get the same one-click jump the client pages had.
 */
export function TopBarClientSwitcher({
  variant = "desktop",
}: {
  variant?: "desktop" | "mobile" | "drawer";
}) {
  const { client, demoMode } = useClientSidebar();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [allClients, setAllClients] = useState<ClientListEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const { recentClients, pruneRecentClients } = useRecentClients();
  const searchParams = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const mobile = variant === "mobile";
  const drawer = variant === "drawer";

  const clientId = client?.clientId ?? null;
  const clientName = client?.clientName ?? null;

  // Demo mode masks the name everywhere it is shown, so it has to be what we
  // match against too - typing the masked name a user can actually see must
  // find the row.
  const displayName = useCallback(
    (name: string) => (demoMode ? anonymizeClientName(name) : name),
    [demoMode],
  );

  useEffect(() => {
    if (!allClients) return;
    pruneRecentClients(new Set(allClients.map((c) => c.id)));
  }, [allClients, pruneRecentClients]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function close() {
      setOpen(false);
      setSearch("");
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      close();
      triggerRef.current?.focus();
    }
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    }

    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  async function loadClientsIfNeeded() {
    if (allClients !== null || loading) return;
    setLoading(true);
    try {
      setAllClients(await loadClientList());
    } finally {
      setLoading(false);
    }
  }

  function closeSwitcher() {
    setOpen(false);
    setSearch("");
  }

  function toggleSwitcher() {
    if (open) {
      closeSwitcher();
      return;
    }
    setOpen(true);
    void loadClientsIfNeeded();
  }

  function buildHref(targetId: string): string {
    // The bare company route resolves the landing per viewer server-side
    // (staff → Pulse, client users → their permitted landing page).
    const base = `/clients/${targetId}`;
    const params = new URLSearchParams();
    const range = searchParams.get("range");
    const compare = searchParams.get("compare");
    if (range) params.set("range", range);
    if (compare) params.set("compare", compare);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  const needle = search.toLowerCase();
  const filteredClients = allClients?.filter((c) =>
    displayName(c.name).toLowerCase().includes(needle),
  );

  const accessibleClientById = useMemo(
    () => new Map(allClients?.map((c) => [c.id, c]) ?? []),
    [allClients],
  );

  const filteredRecent = allClients
    ? recentClients.flatMap((c) => {
        const accessibleClient = accessibleClientById.get(c.id);
        if (!accessibleClient || c.id === clientId) return [];
        return displayName(accessibleClient.name).toLowerCase().includes(needle)
          ? [accessibleClient]
          : [];
      })
    : [];

  const triggerLabel = clientName ? displayName(clientName) : "Select company";

  return (
    <div
      ref={containerRef}
      data-tour="client-switcher"
      className={`relative ${mobile || drawer ? "min-w-0" : ""} ${drawer ? "w-full" : ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleSwitcher}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={clientName ? `Switch company, currently ${triggerLabel}` : "Select a company"}
        className={`flex items-center gap-1.5 rounded-md text-sm transition-colors ${
          drawer
            ? "min-h-11 w-full px-3"
            : mobile
              ? "h-11 min-w-0 max-w-full px-2"
              : "h-8 max-w-[14rem] px-2"
        } ${
          clientName
            ? "font-semibold text-white hover:bg-white/[.08]"
            : "font-medium text-[#AAB2BD] hover:bg-white/[.06] hover:text-white"
        }`}
      >
        <svg
          width={15}
          height={15}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 text-[#AAB2BD]"
          aria-hidden
        >
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M19 21V11l-6-4" />
        </svg>
        <span className="truncate">{triggerLabel}</span>
        <svg
          width={13}
          height={13}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 text-[#AAB2BD] transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className={`z-[120] flex max-h-[26rem] flex-col overflow-hidden rounded-lg border border-spark-light-gray bg-white shadow-[var(--shadow-card-hover)] ${
          mobile
            ? "fixed inset-x-3 top-[calc(var(--topbar-h)+0.5rem)]"
            : drawer
              ? "absolute left-0 right-0 top-full mt-2"
              : "absolute right-0 top-full mt-2 w-72"
        }`}>
          <div className="border-b border-spark-light-gray p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-spark-border bg-white px-3 py-2 text-sm text-spark-black placeholder:text-spark-gray focus:border-spark-blue focus:outline-none"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredRecent.length > 0 && (
              <div>
                <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[.07em] text-spark-gray">
                  Recent
                </p>
                {filteredRecent.map((c) => (
                  <Link
                    key={c.id}
                    href={buildHref(c.id)}
                    onClick={closeSwitcher}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-spark-charcoal transition-colors hover:bg-spark-hover"
                  >
                    <span className="truncate">{displayName(c.name)}</span>
                  </Link>
                ))}
              </div>
            )}

            <div>
              <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[.07em] text-spark-gray">
                All Companies
              </p>
              {loading && <div className="px-3 py-2 text-xs text-spark-gray">Loading...</div>}
              {filteredClients?.map((c) => (
                <Link
                  key={c.id}
                  href={buildHref(c.id)}
                  onClick={closeSwitcher}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                    c.id === clientId
                      ? "bg-spark-blue-tint font-semibold text-spark-blue-text"
                      : "text-spark-charcoal hover:bg-spark-hover"
                  }`}
                >
                  <span className="truncate">{displayName(c.name)}</span>
                  {c.status === "inactive" && (
                    <span className="ml-auto shrink-0 text-[10px] text-spark-gray">inactive</span>
                  )}
                </Link>
              ))}
              {filteredClients && filteredClients.length === 0 && !loading && (
                <div className="px-3 py-2 text-xs text-spark-gray">No results</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
